import React, { Suspense, useContext, useEffect, useRef, useState } from 'react'
import { Award, IAward, IIndustry, IOccupation } from '../../../models/Award'
import Conditional from '../../common/Conditional'
import FormErrorDisplay from '../../common/FormErrorMessages'
import {
  IFindMyAwardStep,
  FindMyAwardWizardSteps,
} from './FindMyAwardWizardBreadcrumbs'
import useErrorHandler from '../../../hooks/useErrorHandler'
import { Form, Formik } from 'formik'
import LoaderContext, { LoaderProvider } from '../../context/FormLoader'
import * as Config from '../../../public/external-links'
import { useHistory } from 'react-router-dom'
import ErrorFallback from '../../common/ErrorFallback'
import { ErrorBoundary } from 'react-error-boundary'
import { FindMyAwardLayout } from './FindMyAwardLayout'
import { ICallout } from '../../../models/Callout'
import useCallout from '../../../hooks/useCallout'
import CalloutDisplay from '../../common/CalloutDisplay'

const FindMyAwardWizardBreadcrumbs = React.lazy(
  () => import('./FindMyAwardWizardBreadcrumbs')
)
const StepOneAwardCoverage = React.lazy(() => import('./StepOneAwardCoverage'))
const StepTwoRoleIndustry = React.lazy(() => import('./StepTwoRoleIndustry'))
const StepThreeYourAward = React.lazy(() => import('./StepThreeYourAward'))

const FindMyAwardWizard = () => {
  const mountedRef = useRef(false)
  const formRef = useRef(null)
  const [steps, setSteps] = useState<IFindMyAwardStep[]>()
  const [activeStep, setActiveStep] = useState<number>(null)
  const [pageHeading, setPageHeading] = useState('')
  const [userErrors, handleErrors] = useErrorHandler()
  const [award, setAward] = useState<IAward>(new Award())
  const history = useHistory()
  const [data, setData] = useState<ICallout[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [getCallout] = useCallout('FindMyAward', 'Before')
  const loader = useContext(LoaderContext)
  const indicatorRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      setActiveStep(1)
    }

    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (activeStep) {
      const steps = getSteps(activeStep)

      if (mountedRef.current) {
        setPageHeading(steps[activeStep].heading)
        setSteps(steps)
      }
    }
  }, [activeStep])

  const handleOccupationChange = (occupation: IOccupation) => {
    setAward({ ...award, occupation: occupation.name })
  }

  const handleIndustryChange = (industry: IIndustry) => {
    setAward({
      ...award,
      industry: industry.name,
      awardCode: industry.awardCode,
      awardName: industry.awardName,
    })
  }

  const handleRegisterAccount = () => {
    history.push('/my-account/register')
  }

  const handleViewAward = () => {
    const awardSummaryUrl = `${Config.AWARD_SUMMARY}/${award.awardCode}-summary`
    window.location.href = awardSummaryUrl
  }

  const handleAddAward = () => {
    const awardSummaryUrl = `${Config.AWARD_SUMMARY}/${award.awardCode}-summary`

    const query = new URLSearchParams({
      System: 'Award',
      Title: `${award.awardName}  [${award.awardCode}]`,
      SourceURL: `${Config.AWARD_VIEWER}${award.awardCode}`,
      SummaryTitle: `${award.awardName}  summary`,
      SummaryURL: awardSummaryUrl,
    })
    const url = '/my-account/BookmarkToMyAccount?' + query.toString()

    history.push(url)
  }

  const handleSubmit = () => {
    return
  }

  const getSteps = (current: number) => {
    const tempSteps = FindMyAwardWizardSteps.slice()

    // reset
    tempSteps.map((step) => {
      step.current = false
      step.done = false
    })

    for (let index = 0; index < current; index++) {
      tempSteps[index].done = true
      tempSteps[index].current = false
    }
    tempSteps[current].current = true
    tempSteps[current].done = false

    return tempSteps
  }

  useEffect(() => {
    mountedRef.current = true
    loader.up()
    if (mountedRef.current) {
      getCallout()
        .then((response) => {
          if (mountedRef.current) {
            setData(response.callouts)
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setIsLoading(false)
          loader.down()
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  const setIndicatorFocus = () => {
    indicatorRef?.current?.focus()
  }

  return (
    <>
      <Suspense fallback={null}>
        <FindMyAwardLayout>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <h1>Find my award</h1>
            <Conditional if={!isLoading}>
              {data &&
                data.map((c, i) => (
                  <CalloutDisplay
                    key={i}
                    className=""
                    content={c.content}
                    name="FindMyAward"
                  />
                ))}
            </Conditional>

            <p>
              Use this 3-step form to help find the right award that you&apos;re
              covered by.
            </p>

            <FindMyAwardWizardBreadcrumbs
              steps={steps}
              indicatorRef={indicatorRef}
            />
            <div className="awards-finder tile-award">
              <Conditional if={activeStep > 0}>
                <h2>{pageHeading}</h2>
              </Conditional>

              <FormErrorDisplay
                show={userErrors?.length > 0}
                errorsList={userErrors}
                heading="Please review the following errors"
              />

              <LoaderProvider>
                <Conditional if={activeStep > 0}>
                  <Formik
                    innerRef={formRef}
                    enableReinitialize
                    initialValues={award}
                    onSubmit={() => {
                      handleSubmit
                    }}
                  >
                    {({ handleSubmit }) => (
                      <Form
                        className="ui form osp-variant large"
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        id="find-my-award"
                      >
                        <Conditional if={activeStep === 1}>
                          <StepOneAwardCoverage
                            registeredAgreement={
                              award.registeredAgreement || false
                            }
                            onError={handleErrors}
                            onNext={() => {
                              setActiveStep(activeStep + 1)
                              setIndicatorFocus()
                            }}
                          />
                        </Conditional>

                        <Conditional if={activeStep > 1}>
                          <StepTwoRoleIndustry
                            onError={handleErrors}
                            onBeforeChange={() => setActiveStep(2)}
                            onIndustryChange={handleIndustryChange}
                            onOccupationChange={handleOccupationChange}
                            onNext={() => {
                              setActiveStep(activeStep + 1)
                              setIndicatorFocus()
                            }}
                          />

                          <Conditional if={activeStep === 3}>
                            <StepThreeYourAward
                              award={award}
                              onError={handleErrors}
                              onViewAward={handleViewAward}
                              onAddAward={handleAddAward}
                              onRegisterAccount={handleRegisterAccount}
                              onRestart={(e) => {
                                e.stopPropagation()
                                setActiveStep(1)
                              }}
                            />
                          </Conditional>
                        </Conditional>
                      </Form>
                    )}
                  </Formik>
                </Conditional>
              </LoaderProvider>
            </div>
          </ErrorBoundary>
        </FindMyAwardLayout>
      </Suspense>
    </>
  )
}

export default FindMyAwardWizard
